import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Prismic from "@prismicio/client";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import "../Css/caseStudy.css";

// Same option-card pattern used in the case-study page's own multi-case
// picker modal — duplicated here per this codebase's convention of not
// sharing small components across files.
function MultiCaseOptionCard({ link, fallbackUrl, title, description, image, onNavigate }) {
  const isDocument = link?.link_type === "Document" && link?.uid;
  if (!isDocument && !fallbackUrl) return null;

  const cardContent = (
    <>
      <img className="multi-case-card-image" src={image} alt={title} />
      <div className="multi-case-card-footer">
        <div className="multi-case-card-tag">
          <span className="multi-case-card-dot" />
          <span>{title}</span>
        </div>
        <h3 className="multi-case-card-heading">{description}</h3>
      </div>
    </>
  );

  return isDocument ? (
    <Link to={`/case-study/${link.uid}`} className="multi-case-card" onClick={onNavigate}>
      {cardContent}
    </Link>
  ) : (
    <a
      href={fallbackUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="multi-case-card"
      onClick={onNavigate}
    >
      {cardContent}
    </a>
  );
}

// A small pixel-art padlock (same blocky <rect>-grid technique as the
// drawer row's arrow icon) shown in front of NDA/locked entries.
function PixelLockIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 7 8"
      shapeRendering="crispEdges"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="0" width="5" height="1" fill="currentColor" />
      <rect x="0" y="1" width="1" height="1" fill="currentColor" />
      <rect x="6" y="1" width="1" height="1" fill="currentColor" />
      <rect x="0" y="2" width="1" height="1" fill="currentColor" />
      <rect x="6" y="2" width="1" height="1" fill="currentColor" />
      <rect x="0" y="3" width="7" height="1" fill="currentColor" />
      <rect x="0" y="4" width="7" height="1" fill="currentColor" />
      <rect x="0" y="5" width="7" height="1" fill="currentColor" />
      <rect x="0" y="6" width="3" height="1" fill="currentColor" />
      <rect x="4" y="6" width="3" height="1" fill="currentColor" />
      <rect x="0" y="7" width="7" height="1" fill="currentColor" />
    </svg>
  );
}

// One row in the "browse all case studies" drawer — same link branching as
// the case-study page's own OtherCaseStudyCard (external link, multi-case-
// study picker, or a direct case-study link) but as a plain text row with a
// thumbnail. NDA entries are included (unlike the case-study page's random
// pick pool) but rendered as a non-clickable locked row instead.
function CaseStudyDrawerItem({ entry, onOpenMultiModal, onNavigate }) {
  const { source, item: e } = entry;
  const isMain = source === "main";
  const title = e?.data?.title;
  const thumb = e?.data?.images?.[0]?.image_link;
  const isLocked = Boolean(e?.data?.nda);

  const rowContent = (
    <>
      {thumb && (
        <span className="cs-drawer-item-thumb" aria-hidden="true">
          <img src={thumb} alt="" />
        </span>
      )}
      <span className="cs-drawer-item-title">{title}</span>
      {isLocked ? (
        <PixelLockIcon className="cs-drawer-item-lock" />
      ) : (
        <svg
          className="cs-other-card-arrow-icon cs-drawer-item-arrow"
          viewBox="0 0 7 5"
          shapeRendering="crispEdges"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect x="3" y="0" width="1" height="1" fill="currentColor" />
          <rect x="4" y="1" width="1" height="1" fill="currentColor" />
          <rect x="0" y="2" width="7" height="1" fill="currentColor" />
          <rect x="4" y="3" width="1" height="1" fill="currentColor" />
          <rect x="3" y="4" width="1" height="1" fill="currentColor" />
        </svg>
      )}
    </>
  );

  if (isLocked) {
    return (
      <div className="cs-drawer-item cs-drawer-item-static" aria-disabled="true">
        {rowContent}
      </div>
    );
  }

  if (isMain && e?.data?.onclicklink) {
    return (
      <a
        href={e?.data?.link}
        target="_blank"
        rel="noopener noreferrer"
        className="cs-drawer-item"
      >
        {rowContent}
      </a>
    );
  }

  const multi = isMain
    ? e?.data?.multi_case_studies?.[0]
    : e?.data?.multi_case_study?.[0];
  const isMultiEnabled = isMain ? multi?.multi_case_studies1 : multi?.enable;

  if (isMultiEnabled) {
    return (
      <button
        type="button"
        className="cs-drawer-item"
        onClick={() => onOpenMultiModal(entry)}
      >
        {rowContent}
      </button>
    );
  }

  const uid = e?.data?.content_relationship?.uid;
  if (!uid) return null;

  return (
    <Link
      to={`/case-study/${uid}`}
      className="cs-drawer-item"
      onClick={onNavigate}
    >
      {rowContent}
    </Link>
  );
}

// The "browse all case studies" drawer — same slide-in-from-right panel
// used on the case-study detail page's "View All Case Studies" CTA, but
// standalone/controlled so any page (About, Archives, …) can trigger it
// from its own mobile menu without needing a "current case study" to
// exclude.
export default function CaseStudyBrowseDrawer({ isOpen, onClose }) {
  const [otherWorklistDocs, setOtherWorklistDocs] = useState(null);
  const [otherMoreworkDocs, setOtherMoreworkDocs] = useState(null);
  const [multiModalItem, setMultiModalItem] = useState(null);

  useEffect(() => {
    const apiEndpoint = "https://harshitsharma.prismic.io/api/v2";
    const accessToken =
      "MC5ZYUVtbXhNQUFETUFuODZN.77-977-9Ye-_ve-_vX8o77-977-977-977-977-9a--_ve-_ve-_ve-_ve-_ve-_vQITBF5h77-977-977-977-977-977-9TO-_vQ";
    const client = Prismic.client(apiEndpoint, { accessToken });

    const fetchCaseStudies = async () => {
      const [worklistRes, moreworkRes] = await Promise.all([
        client.query(Prismic.Predicates.at("document.type", "worklist"), {
          orderings: "[my.worklist.order desc]",
        }),
        client.query(Prismic.Predicates.at("document.type", "morework"), {
          orderings: "[my.morework.order desc]",
        }),
      ]);
      if (worklistRes) setOtherWorklistDocs(worklistRes.results);
      if (moreworkRes) setOtherMoreworkDocs(moreworkRes.results);
    };

    fetchCaseStudies();
  }, []);

  // NDA/locked entries are included (shown locked, not linkable) rather
  // than filtered out — this is a full browse list, not a "pick one to
  // feature" pool.
  const allCaseStudiesForDrawer = useMemo(() => {
    const main = (otherWorklistDocs || []).map((item) => ({
      source: "main",
      item,
    }));
    const more = (otherMoreworkDocs || []).map((item) => ({
      source: "more",
      item,
    }));
    return [...main, ...more].filter(({ source, item }) => {
      if (item?.data?.nda) return true;

      const uid = item?.data?.content_relationship?.uid;
      if (uid) return true;

      const isMain = source === "main";
      if (isMain && item?.data?.onclicklink) return true;
      const multi = isMain
        ? item?.data?.multi_case_studies?.[0]
        : item?.data?.multi_case_study?.[0];
      return Boolean(isMain ? multi?.multi_case_studies1 : multi?.enable);
    });
  }, [otherWorklistDocs, otherMoreworkDocs]);

  useEffect(() => {
    const shouldLock = isOpen || Boolean(multiModalItem);
    document.body.style.overflow = shouldLock ? "hidden" : "";
    document.documentElement.style.overflow = shouldLock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isOpen, multiModalItem]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (evt) => {
      if (evt.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={`cs-drawer-backdrop ${isOpen ? "visible" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={`cs-drawer ${isOpen ? "open" : ""}`} aria-hidden={!isOpen}>
        <div className="cs-drawer-header">
          <h2 className="cs-drawer-heading">All Case Studies</h2>
          <button type="button" className="multi-case-modal-close" onClick={onClose}>
            <span className="multi-case-modal-close-dot" />
            Close
          </button>
        </div>
        <div className="cs-drawer-list">
          {allCaseStudiesForDrawer.map((entry) => (
            <CaseStudyDrawerItem
              key={`${entry.source}-${entry.item.id}`}
              entry={entry}
              onOpenMultiModal={(item) => {
                setMultiModalItem(item);
                onClose();
              }}
              onNavigate={onClose}
            />
          ))}
        </div>
      </aside>

      <Modal
        open={Boolean(multiModalItem)}
        onClose={() => setMultiModalItem(null)}
        center
        showCloseIcon={false}
        blockScroll={false}
        classNames={{
          modal: "multi-case-modal",
          modalAnimationIn: "multi-case-slide-in",
          modalAnimationOut: "multi-case-slide-out",
        }}
      >
        <div className="multi-case-modal-topbar">
          <h2 className="multi-case-modal-heading">Choose case study to view</h2>
          <button
            type="button"
            className="multi-case-modal-close"
            onClick={() => setMultiModalItem(null)}
          >
            <span className="multi-case-modal-close-dot" />
            Close
          </button>
        </div>
        <div className="multi-case-modal-grid">
          {(() => {
            if (!multiModalItem) return null;
            const { source, item: e } = multiModalItem;
            const isMain = source === "main";
            const multi = isMain
              ? e?.data?.multi_case_studies?.[0]
              : e?.data?.multi_case_study?.[0];
            return (
              <>
                <MultiCaseOptionCard
                  link={
                    isMain ? multi?.case_study_link_1 : multi?.content_relationship_1
                  }
                  fallbackUrl={multi?.link_1}
                  title={multi?.title_1}
                  description={multi?.description_1}
                  image={multi?.image_1}
                  onNavigate={() => setMultiModalItem(null)}
                />
                <MultiCaseOptionCard
                  link={
                    isMain
                      ? multi?.case_study_link_2
                      : multi?.content_relationshiop_2
                  }
                  fallbackUrl={multi?.link_2}
                  title={multi?.title_2}
                  description={multi?.description_2}
                  image={multi?.image_2}
                  onNavigate={() => setMultiModalItem(null)}
                />
              </>
            );
          })()}
        </div>
      </Modal>
    </>
  );
}
